/**
 * Shared presentation helpers for Phase 2 `inventoryAvailability` on listing payloads.
 * Keeps card/detail copy aligned with backend `listingAvailabilityState`.
 */

const KIND_OPTIONS = ['unit', 'area', 'capacity', 'time', 'hybrid'];

function str(v) {
  if (v == null) return '';
  return String(v).trim();
}

function num(v) {
  const n = parseFloat(String(v).replace(/,/g, ''), 10);
  return Number.isFinite(n) ? n : NaN;
}

function txNorm(transactionType) {
  return String(transactionType || 'lease').toLowerCase();
}

/**
 * Maps listing transaction type to seller inventory API `transactionScope`.
 * @param {string} transactionType
 * @returns {'lease'|'sale'|'both'}
 */
export function inventoryTransactionScopeFromForm(transactionType) {
  const t = txNorm(transactionType);
  if (t === 'sale') return 'sale';
  if (t === 'lease_to_own') return 'both';
  return 'lease';
}

/**
 * Build POST body for `POST .../my-published/:listingId/inventory` from list-asset form fields.
 * @param {object} form
 * @returns {object}
 */
export function buildSellerInventoryPostBodyFromListingForm(form) {
  const kind = str(form.inventoryKind).toLowerCase() || 'unit';
  const transactionScope = inventoryTransactionScopeFromForm(form.transactionType);
  const body = {
    inventoryKind: kind,
    isDefault: true,
    areaUom: str(form.inventoryAreaUom) || 'sqft',
    transactionScope,
    label: str(form.inventoryLabel) ? str(form.inventoryLabel).slice(0, 255) : null,
  };
  const capUom = str(form.inventoryCapacityUom);
  if (capUom) body.capacityUom = capUom.slice(0, 64);

  if (kind === 'unit') {
    body.totalUnits = Math.max(0, num(form.inventoryTotalUnits));
  } else if (kind === 'area' || kind === 'time') {
    body.totalArea = Math.max(0, num(form.inventoryTotalArea));
  } else if (kind === 'capacity') {
    body.totalCapacity = Math.max(0, num(form.inventoryTotalCapacity));
  } else if (kind === 'hybrid') {
    const u = num(form.inventoryTotalUnits);
    const a = num(form.inventoryTotalArea);
    const c = num(form.inventoryTotalCapacity);
    if (Number.isFinite(u) && u > 0) body.totalUnits = u;
    if (Number.isFinite(a) && a > 0) body.totalArea = a;
    if (Number.isFinite(c) && c > 0) body.totalCapacity = c;
    const hybrid = {
      schema: 'listing_wizard_v1',
      partialLeasingAllowed: Boolean(form.inventoryPartialLeasingAllowed),
    };
    if (str(form.inventoryHybridNote)) {
      hybrid.note = str(form.inventoryHybridNote).slice(0, 500);
    }
    const minU = str(form.inventoryMinLeasableUnits) ? num(form.inventoryMinLeasableUnits) : null;
    if (minU != null && Number.isFinite(minU)) hybrid.minLeasableUnits = minU;
    const minA = str(form.inventoryMinLeasableArea) ? num(form.inventoryMinLeasableArea) : null;
    if (minA != null && Number.isFinite(minA)) hybrid.minLeasableArea = minA;
    body.hybridDimensions = hybrid;
  }

  return body;
}

/**
 * Validate inventory section when seller opts in (`inventoryTrackEnabled`).
 * @param {object} f form
 * @returns {string[]} error messages
 */
export function validateListingFormInventorySection(f) {
  const errors = [];
  if (!f?.inventoryTrackEnabled) return errors;

  const kind = str(f.inventoryKind).toLowerCase();
  if (!KIND_OPTIONS.includes(kind)) {
    errors.push('Select a valid inventory model (unit, area, capacity, time, or hybrid).');
    return errors;
  }

  if (kind === 'unit') {
    const n = num(f.inventoryTotalUnits);
    if (!str(f.inventoryTotalUnits) || !Number.isFinite(n) || n < 1) {
      errors.push('Inventory: total units must be at least 1.');
    }
  } else if (kind === 'area' || kind === 'time') {
    const n = num(f.inventoryTotalArea);
    if (!str(f.inventoryTotalArea) || !Number.isFinite(n) || n <= 0) {
      errors.push(
        kind === 'time'
          ? 'Inventory: total leasable area (sq ft) must be a positive number for time-based inventory.'
          : 'Inventory: total area must be a positive number.',
      );
    }
  } else if (kind === 'capacity') {
    const n = num(f.inventoryTotalCapacity);
    if (!str(f.inventoryTotalCapacity) || !Number.isFinite(n) || n <= 0) {
      errors.push('Inventory: total capacity must be a positive number.');
    }
    if (!str(f.inventoryCapacityUom)) {
      errors.push('Inventory: capacity unit is required (e.g. tons, pallets).');
    }
  } else if (kind === 'hybrid') {
    const u = str(f.inventoryTotalUnits) ? num(f.inventoryTotalUnits) : NaN;
    const a = str(f.inventoryTotalArea) ? num(f.inventoryTotalArea) : NaN;
    const c = str(f.inventoryTotalCapacity) ? num(f.inventoryTotalCapacity) : NaN;
    const any =
      (Number.isFinite(u) && u > 0) || (Number.isFinite(a) && a > 0) || (Number.isFinite(c) && c > 0);
    if (!any) {
      errors.push('Inventory (hybrid): enter at least one positive total among units, area, or capacity.');
    }
  }

  const minU = str(f.inventoryMinLeasableUnits) ? num(f.inventoryMinLeasableUnits) : null;
  if (minU != null && (!Number.isFinite(minU) || minU < 0)) {
    errors.push('Inventory: minimum leasable quantity must be a non-negative number.');
  }
  const minA = str(f.inventoryMinLeasableArea) ? num(f.inventoryMinLeasableArea) : null;
  if (minA != null && (!Number.isFinite(minA) || minA < 0)) {
    errors.push('Inventory: minimum leasable area must be a non-negative number.');
  }

  return errors;
}

/**
 * @param {object} listing — card or detail with optional `inventoryAvailability`
 * @returns {{ label: string, className: string } | null}
 */
export function getSearchCardAvailabilityBadge(listing) {
  const ia = listing?.inventoryAvailability;
  if (!ia || typeof ia !== 'object') return null;

  const tx = txNorm(listing?.transactionType);
  const state = ia.listingAvailabilityState;

  if (state === 'not_tracked') {
    return null;
  }

  const saleExhausted = tx === 'sale' && state === 'fully_leased';

  const labels = {
    available: 'Available',
    partially_available: 'Partially available',
    fully_leased: saleExhausted ? 'Sold out' : 'Fully leased',
    blocked: 'Unavailable',
  };

  const label = labels[state] || null;
  if (!label) return null;

  const classMap = {
    available: 'ile-search-badge--avail-available',
    partially_available: 'ile-search-badge--avail-partial',
    fully_leased: saleExhausted ? 'ile-search-badge--avail-sold' : 'ile-search-badge--avail-full',
    blocked: 'ile-search-badge--avail-blocked',
  };

  return { label, className: classMap[state] || 'ile-search-badge--avail-neutral' };
}

/**
 * Detail / seller snapshot: primary headline for availability.
 * @param {object} listing
 * @returns {{ headline: string, sub?: string, badgeClass: string } | null}
 */
export function getListingAvailabilityHeadline(listing) {
  const ia = listing?.inventoryAvailability;
  if (!ia || typeof ia !== 'object') return null;
  const tx = txNorm(listing?.transactionType);
  const state = ia.listingAvailabilityState;
  if (state === 'not_tracked') {
    if (Array.isArray(ia.bookedRanges) && ia.bookedRanges.length > 0) {
      return {
        headline: 'Active bookings',
        sub: 'Some dates may already be committed — confirm with the lister.',
        badgeClass: 'ile-avail-pill--partial',
      };
    }
    return null;
  }
  const saleExhausted = tx === 'sale' && state === 'fully_leased';
  const map = {
    available: { headline: 'Available', badgeClass: 'ile-avail-pill--ok' },
    partially_available: { headline: 'Partially available', badgeClass: 'ile-avail-pill--partial' },
    fully_leased: {
      headline: saleExhausted ? 'Sold out' : 'Fully leased',
      badgeClass: saleExhausted ? 'ile-avail-pill--full' : 'ile-avail-pill--full',
    },
    blocked: { headline: 'Unavailable', sub: 'Blocked or maintenance hold', badgeClass: 'ile-avail-pill--blocked' },
  };
  const row = map[state];
  if (!row) return null;
  return row;
}

function formatQty(n, suffix = '') {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const t = v % 1 === 0 ? v.toLocaleString('en-IN') : v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return suffix ? `${t} ${suffix}` : t;
}

/**
 * @param {object} listing
 * @returns {string[]}
 */
export function getInventoryLineSummaryBullets(listing) {
  const ia = listing?.inventoryAvailability;
  if (!ia?.inventoryLines || !Array.isArray(ia.inventoryLines) || ia.inventoryLines.length === 0) {
    return [];
  }
  const lines = [];
  for (const ln of ia.inventoryLines) {
    const parts = [];
    const kind = str(ln.kind);
    if (ln.totalUnits != null && Number.isFinite(Number(ln.totalUnits))) {
      const tot = Number(ln.totalUnits);
      const av = ln.effectiveAvailableUnits != null ? Number(ln.effectiveAvailableUnits) : null;
      parts.push(`Units: ${formatQty(av)} / ${formatQty(tot)} available`);
    }
    if (ln.totalArea != null && Number.isFinite(Number(ln.totalArea))) {
      const tot = Number(ln.totalArea);
      const av = ln.effectiveAvailableArea != null ? Number(ln.effectiveAvailableArea) : null;
      const uom = str(ln.areaUom) || 'sqft';
      parts.push(`Area: ${formatQty(av)} / ${formatQty(tot)} ${uom} available`);
    }
    if (ln.totalCapacity != null && Number.isFinite(Number(ln.totalCapacity))) {
      const tot = Number(ln.totalCapacity);
      const av = ln.effectiveAvailableCapacity != null ? Number(ln.effectiveAvailableCapacity) : null;
      const uom = str(ln.capacityUom) || '';
      parts.push(`Capacity: ${formatQty(av)} / ${formatQty(tot)}${uom ? ` ${uom}` : ''} available`);
    }
    if (kind === 'time' && parts.length === 0) {
      parts.push('Time-based slot — see booked ranges for committed periods.');
    }
    const label = str(ln.label);
    const prefix = label ? `${label}: ` : '';
    if (parts.length) {
      lines.push(prefix + parts.join(' · '));
    }
  }
  return lines;
}

/**
 * @param {{ start: string, end: string }[]} bookedRanges
 * @param {number} maxLines
 * @returns {string[]}
 */
export function formatBookedRangesSummaryLines(bookedRanges, maxLines = 4) {
  if (!Array.isArray(bookedRanges) || bookedRanges.length === 0) return [];
  const sorted = [...bookedRanges].sort((a, b) => String(a.start).localeCompare(String(b.start)));
  const out = [];
  for (let i = 0; i < sorted.length && i < maxLines; i++) {
    const r = sorted[i];
    const s = str(r?.start);
    const e = str(r?.end);
    if (s && e) out.push(`${s} → ${e}`);
  }
  if (sorted.length > maxLines) {
    out.push(`+ ${sorted.length - maxLines} more period(s)`);
  }
  return out;
}

/**
 * Single-line snapshot for seller cards (uses first inventory line when managed).
 * @param {object} inventoryAvailability
 * @returns {string | null}
 */
export function getOwnerInventorySnapshotLine(inventoryAvailability) {
  const ia = inventoryAvailability;
  if (!ia || ia.inventoryManaged !== true || !ia.inventoryLines?.length) return null;
  const ln = ia.inventoryLines[0];
  const bullets = getInventoryLineSummaryBullets({ inventoryAvailability: ia });
  return bullets[0] || null;
}

/**
 * Soft hint: day after latest booked `end` (not a full calendar engine).
 * @param {{ start: string, end: string }[]} bookedRanges
 * @returns {string | null} ISO date yyyy-mm-dd
 */
export function estimateEarliestCapacityAfterBookings(bookedRanges) {
  if (!Array.isArray(bookedRanges) || bookedRanges.length === 0) return null;
  let maxEnd = '';
  for (const r of bookedRanges) {
    const e = str(r?.end);
    if (e && e > maxEnd) maxEnd = e;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(maxEnd)) return null;
  const d = new Date(`${maxEnd}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
